import { computed, linkedSignal, Signal } from '@angular/core';
import { ResourceSnapshot } from '@angular/core';

/**
 * Keeps the last successfully resolved value of a resource.
 *
 * When a load fails, Angular replaces the resolved value with an error one, so
 * reading `value()` throws and everything derived from it is lost. This keeps
 * showing the last good value instead (e.g. when the connection drops in the
 * middle of a refresh), which is what the view models should display.
 *
 * The `key` identifies the current request (usually the params of the
 * resource). When it changes, the retained value is dropped: showing the data of
 * a previous entity as if it were the new one would be worse than showing
 * nothing. Omit it for resources whose request never changes per entity.
 *
 * The error state is not hidden: read `error()` / `isLoading()` from the
 * resource itself to render it.
 */
export function keepLastResolvedValue<T, K = undefined>(
  snapshot: Signal<ResourceSnapshot<T>>,
  key?: Signal<K>,
): Signal<T | undefined> {
  const state = linkedSignal<
    { snapshot: ResourceSnapshot<T>; key: K | undefined },
    { key: K | undefined; value: T | undefined }
  >({
    source: () => ({ snapshot: snapshot(), key: key?.() }),
    computation: ({ snapshot, key: currentKey }, previous) => {
      const isSameRequest = previous !== undefined && previous.value.key === currentKey;

      if (snapshot.status === 'error') {
        return { key: currentKey, value: isSameRequest ? previous.value.value : undefined };
      }

      return { key: currentKey, value: snapshot.value };
    },
  });

  return computed(() => state().value);
}
